import React from "react";
import { StyleTag } from "seed/helpers";
import { useTheme } from "components/util/helpers/providers/ThemeProvider";

function StyleColor({ 
	theme: propTheme = {}, 
	backgroundHeight = "100vh" 
}) {

	const contextTheme = useTheme({ checkContext: false });
	const theme = { ...contextTheme, ...propTheme }; // Prioritize prop theme over context theme
	let primaryColor = theme.primaryColor ?? "#2080e5";
	let accentColor = theme.accentColor ?? "#f0b000";
	let darkColor = theme.darkColor ?? getDarkerColor(primaryColor, -30);
	let lightColor = theme.lightColor ?? getDarkerColor(primaryColor, 30);
	let accentDarkColor = theme.accentDarkColor ?? getDarkerColor(accentColor, -30);

	return <div>
		{theme.primaryColor || theme.accentColor ?
			<StyleTag content={`

		  /*
			** General components
			*/

			/* Background */

			#background-hero .path-a {
				fill: ${darkColor} !important;
			}
			#background-hero .path-b {
				fill: ${primaryColor} !important;
			}
			#background-hero .path-c {
				fill: ${accentColor} !important;
			}

			/* Buttons & Text */

			.btn.btn-primary {
				background: ${primaryColor} !important;
				border-color: ${primaryColor} !important;
			}
			.btn.btn-primary:hover {
				background: ${darkColor} !important;
			}			
			.btn.btn-outline-primary {
				border-color: ${primaryColor} !important;
				color: ${primaryColor} !important;
			}
			.btn.btn-outline-primary:hover{
				background: ${primaryColor} !important;
				color: white !important;
			}
			.btn.btn-outline-primary-white {
				background: white !important;
				border-color: ${primaryColor} !important;
				color: ${primaryColor} !important;
			}
			.btn.btn-outline-primary-white:hover {
				background: ${primaryColor} !important;
				color: white !important;
			}
			.btn.btn-accent {
				background: ${accentColor} !important;
				border-color: ${accentColor} !important;
			}
			.btn.btn-accent:hover {
				background: ${accentDarkColor} !important;
			}
			.btn.btn-outline-accent {
				border-color: ${accentColor} !important;
				color: ${getDarkerColor(accentColor, -15)} !important;
			}
			.btn.btn-outline-accent:hover{
				background: ${accentColor} !important;
				color: white !important;
			}
			.btn.btn-link {
				color: ${primaryColor} !important;
			}
			.btn.btn-link:hover {
				color: ${darkColor} !important;
			}
			
			.nav-tabs .nav-link.active {
				color: ${primaryColor} !important;
				border-color: ${primaryColor} !important;
			}

			.nav-pills:not(.nav-segment) .nav-link.active {
				background: ${primaryColor} !important;
			}

			.bg-dark {
				background-color: ${darkColor} !important;
			}

			.icon-primary {
				border-color: ${primaryColor} !important;
				background: ${primaryColor} !important;
			}

			.text-primary {
				color: ${primaryColor} !important;
			}
			.text-accent {
				color: ${getDarkerColor(accentColor, -15)} !important;
			}

			input[type=checkbox] {
				accent-color: ${getDarkerColor(accentColor, -15)} !important; 
			}

			.custom-control-input:checked~.custom-control-label::before {
				border-color: ${getDarkerColor(accentColor, -15)} !important;
				background-color: ${getDarkerColor(accentColor, -15)} !important; 
			}

			.step-icon-primary {
				background-color: ${primaryColor} !important;
			}

			.bg-primary {
				background-color: ${primaryColor} !important;
			}
			.bg-accent {
				background-color: ${accentColor} !important;
			}

			.border-primary {
				border-color: ${primaryColor} !important;
			}
			.border-accent {
				border-color: ${accentColor} !important;
			}



			.navbar-nav.nav-tabs .nav-link.active {
				border-left-color: ${primaryColor} !important;
			}
			.navbar .nav-link.active {
				color: ${primaryColor} !important;
			}			

			.badge-primary {
				background: ${primaryColor} !important;
			}
			.badge-accent {
				background: ${accentColor} !important;
			}

			/* Seed components */
			.seed__loading svg {
				stroke: ${primaryColor} !important;
			}

			/* Icons */
			.process-icon-1 { fill:${primaryColor} !important;  }

			.model-icon-1 { stroke:${primaryColor} !important;}
			.model-icon-3 { fill:${primaryColor} !important;}

			.solution-icon-0 { stroke:${primaryColor}a0 !important;}
      .solution-icon-1 { fill:${primaryColor} !important;}
			.solution-icon-2 { fill:${primaryColor} !important;}
			
			/* Pagination */

			.page-item.active .page-link {
				background: ${primaryColor} !important;
				border-color: ${primaryColor} !important;
			}

			.page-item .page-link:hover {
				background: ${darkColor} !important;
				color: white !important;
			}

			/*
			** Specific views
			*/

			/* Schema Form */

			.schema-form .step-form .step-icon, .schema-builder .step-form .step-icon {
				border-color: ${primaryColor}2a !important;
			}

			/* Start */

			#start-templates {
				background: #ffffff !important;
			}

			#start-activity tr th {
				background-color: ${primaryColor}20 !important;
				color: ${primaryColor} !important;
			}
			#start .navbar-vertical-content .nav-link.active i, #start .navbar-vertical-content .nav-link.active,
			#start .navbar-vertical-content .nav-link:hover i, #start .navbar-vertical-content .nav-link:hover {
				border-left-color: ${accentColor} !important;
			} 
			#start .navbar-vertical-content .nav-link.active i, #start .navbar-vertical-content .nav-link.active span, 
			#start .navbar-vertical-content .nav-link:hover i, #start .navbar-vertical-content .nav-link:hover span {
				color: ${getDarkerColor(accentColor, -15)} !important;
			}

			#start #start-content-wrapper #start-flows #start-flows-container #flows #flows-container .flow:hover .h4 {
				color: ${primaryColor} !important;
			}

			#start .template-item {
				border-left-color: ${accentColor} !important;
			}

			#background-hero-template .path-a {
				fill: ${primaryColor}20 !important;
			}
			#background-hero-template .path-b {
				fill: ${primaryColor}20 !important;
			}
			#background-hero-template .path-c {
				fill: ${darkColor}20 !important;
			}

			/* Settings */

			#settings .navbar-vertical-content .nav-link.active i, #settings .navbar-vertical-content .nav-link.active span, 
			#settings .navbar-vertical-content .nav-link:hover i, #settings .navbar-vertical-content .nav-link:hover span{
				color: ${primaryColor} !important
			}


			/* Studio */
			
			#flow-tabs a.active, #flow-tabs a:hover{
				color: ${primaryColor} !important;
				border-bottom-color: ${primaryColor} !important;
			}
			
			#workspace-title .logo-sub label {
				background: ${primaryColor} !important;
			}

			#toolbox-sidenav .btn-app:hover, #toolbox-sidenav .btn-app.active {
				color: ${primaryColor} !important;
			}

			#toolbox-sidenav .btn-app:hover i, #toolbox-sidenav .btn-app.active i {
				background-color: ${primaryColor}17 !important;
			}

			#toolbox-container #toolbox-main #toolbox-actions #toolbox-search .search-cta .search-filters.active {
				color: ${primaryColor} !important;
			}
			#toolbox-container #toolbox-actions #toolbox-tabs .tab-button.active {
				background: ${primaryColor} !important;
			}

			#toolbox-container #toolbox-actions .apps .btn-category[aria-expanded=true] .category-icon,
			#toolbox-container #toolbox-actions .apps .btn-category[aria-expanded=true] .category-title {
				color: ${primaryColor} !important;
			}

			#toolbox-container #toolbox-actions .action-element .btn-action.active {
				background: ${primaryColor} !important;
			}

			#toolbox-container #toolbox-main #toolbox-templates .tab-button.active {
				background: ${primaryColor} !important;
			}

			#toolbox-container #toolbox-actions .custom-switch .custom-control-label::before {
      	border-color: ${accentColor};
    	}

    	#toolbox-container #toolbox-actions .custom-switch .custom-control-label::after {
      	background-color: ${accentColor};
    	}

    	#toolbox-container #toolbox-actions .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
      	background-color: ${accentColor};
    	}


			#template-categories-container .tab-button.active {
				background: ${primaryColor};
			}
			

			/* Studio-board */

			.node .customize-tree-button .tree-name, 
			.node-control .customize-tree-button .tree-name, 
			.trigger .customize-tree-button .tree-name {
				background-color: ${primaryColor}20;
				color: ${primaryColor};				
			}

			.node .customize-tree-button .tree-name:hover, 
			.node-control .customize-tree-button .tree-name:hover, 
			.trigger .customize-tree-button .tree-name:hover {
				background-color: ${getDarkerColor(primaryColor, -15)}3a;
			}


			/* Collection */			

			#collection-tabs .collection-tab.active {
				background: ${primaryColor} !important;
			}

			#collection-tabs .collection-tab:hover {
				background: ${darkColor} !important;
			}

			#collection-tabs .collection-tab.active span,
			#collection-tabs .collection-tab:hover span {
				color: white !important;
			}
			#table-title .edit-btn:hover{
				color: ${primaryColor} !important;
			}

			#tables #tables-body #tables-board #collection-table table td.table-record.active{
				border-color: ${accentColor} !important;
			}

			#tables #tables-body #tables-board #collection-table table thead tr {
				background-color: ${primaryColor}20 !important;
				color: ${primaryColor} !important;
			}

			#tables #tables-body #tables-board #collection-table table .table-column-content,
			#tables #tables-body #tables-board #collection-table table .table-column-content .column-options-icon,
			#tables #tables-body #tables-board #collection-table table .table-column-content .column-type {
				color: ${primaryColor} !important;
			}

			#tables #tables-body #tables-board #collection-table table thead {
				border-bottom: 2px solid ${primaryColor}50 !important;
			}
				


			/* Presentation */
		
			#presentation #presentation-header #presentation-menu .menu-item.active {
				color: ${primaryColor};
			}

			#presentation:has(.view-data.active) #presentation-header #presentation-menu .menu-item.active, 
			#presentation:has(.view-processes.active) #presentation-header #presentation-menu .menu-item.active, 
			#presentation:has(.view-settings.active) #presentation-header #presentation-menu .menu-item.active, 
			#presentation:has(.view-versions.active) #presentation-header #presentation-menu .menu-item.active { 
				background-color: ${primaryColor};
			}

			#presentation #presentation-header .dev-badge>label {
				background-color: ${primaryColor} !important;
			}

			#presentation .animated-container:not(.solution-bg-solid) {
				background: linear-gradient(315deg, #000 0%,
					${getDarkerColor(primaryColor, -15)} 35%,
					${primaryColor} 70%,
					${getDarkerColor(primaryColor, 10)} 100%)
			}

			#presentation .section-view {
				border-right-color: ${primaryColor}40;
			}
			
			
			/* Presentation chat */

			#chat .message {
				border-left-color: ${accentColor} !important;
			}

			#chat .message .sender-name {
				color: ${accentColor} !important;
			}

			#chat .message.user {
				background: ${primaryColor} !important;
			}

			#chat .message.suggestion {
				color: ${getDarkerColor(accentColor, -15)} !important;
				border-color: ${accentColor} !important;
			}

			#chat .message.suggestion.option {
				color: ${primaryColor} !important;
				border-color: ${primaryColor} !important;
			}

			#chat .message.highlighted-message {
				border-color: ${primaryColor} !important;
				font-weight: bold;
			}

			#chat .message-wrapper:not(.user) .message-options .btn-link {
				color: ${getDarkerColor(accentColor, -15)} !important;
			}

			#chat .loading .loader:before {
				background-color:  ${primaryColor} !important;
			}

			#chat .message.inner-toast .loading-icon {
				color: ${primaryColor} !important;
			}

			#chat .input-container input:hover {
				border: 1px solid ${primaryColor}90 !important;
				box-shadow: 0 0 0.25rem ${primaryColor}a0 !important;
			}

			#chat .input-container .send,
			#chat .input-container .send:hover {
				color: ${primaryColor} !important;
			}

			/* Presentation chat - thinking animation */

			#chat .thinking-animation {
				border-left-color: ${accentColor} !important;
			}

			#chat .thinking-animation__pulse-dot {
				background-color: ${accentColor} !important;
			}

			#chat .thinking-animation__label {
				background-image: linear-gradient(
					110deg,
					${accentColor} 0%,
					${accentColor} 24%,
					#fff 38%,
					${accentColor} 52%,
					${accentColor} 72%
				) !important;
				background-size: 260% 100% !important;
				-webkit-background-clip: text !important;
				background-clip: text !important;
			}

			#chat .thinking-streaming__cursor-span {
				background-color: ${accentColor} !important;
			}


			/* Settings */

			#settings tr th {
				background-color: ${primaryColor}20 !important;
				color: ${primaryColor} !important;
			}


			/* Wizard */

			#wizard .chat-messages .message {
				border-left-color: ${primaryColor} !important;
			}

			#brief .step-icon.step-icon-accent {
				background-color: ${accentColor} !important;
			}

			#brief .btn-link.text-white {
				color: #fff !important;
			}

			#agent-loading .agent-step-icon.done {
				background: ${accentColor} !important;
			}
			
			#agent-loading .agent-step.current::after {
				background-color: ${primaryColor} !important
			}

			#generation-subsection-title {
				background-color: ${primaryColor}70 !important
			}

			.product-view-container .content-container h1,
			.product-view-container .content-container h2,
			.product-view-container .content-container h3
			{
				color: ${primaryColor} !important;
			}

			.product-view-container .content-container h3 {
				background: ${primaryColor}10 !important;
			}

		` } />
			: null}

		<StyleTag content={` 
			#background-hero { 	
				display: block !important;				
			}
			#background-hero svg {
			  height: ${backgroundHeight} !important;	
			}
			.animated-container {
				display: block;
			}
		` } />

	</div>

}

function getDarkerColor(color, v) {

	const normalize = (x) =>
		x > 255 ? x - (x - 255) : x;

	const getHex = (x) => {
		if (x < 0 || isNaN(x)) x = 0;
		if (x > 255) x = 255;
		x = x.toString(16);
		return x.length === 1 ? "0" + x : x;
	}

	if (color.length > 6)
		color = color.substring(1, color.length)

	var rgb = parseInt(color, 16);

	var r = Math.abs(((rgb >> 16) & 0xFF) + v);
	r = normalize(r);
	r = getHex(r);

	var g = Math.abs(((rgb >> 8) & 0xFF) + v);
	g = normalize(g);
	g = getHex(g);

	var b = Math.abs((rgb & 0xFF) + v);
	b = normalize(b);
	b = getHex(b);

	return "#" + r + g + b;

}

export default StyleColor;
